import React, { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, RefreshCw, Lightbulb, AlertTriangle, TrendingUp } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useMobile } from "./MobileApp";
import { Glass, ProLock, Rise, SectionTitle, Spinner } from "./ui";

const ICON = { oportunidad: Lightbulb, alerta: AlertTriangle, fortaleza: TrendingUp };
const COLOR = { oportunidad: "text-cyan-300", alerta: "text-amber-400", fortaleza: "text-emerald-400" };
const SUGERIDAS = ["¿Qué producto se vende más?", "¿A qué hora vendo más?", "¿Qué productos debo reponer?", "¿Cómo subo mi ticket promedio?"];

const Insights = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (refresh) => {
    setBusy(true);
    try { setD((await api.get("/mobile/ia/insights", { params: { refresh: refresh || undefined } })).data); } catch (_) { setD({ insights: [] }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(false); }, []);
  return (<>
    <SectionTitle right={<button onClick={() => load(true)} disabled={busy} data-testid="ia-insights-refresh" className="text-slate-500"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></button>}>Insights de hoy</SectionTitle>
    {!d ? <Spinner /> : (
      <div className="space-y-2" data-testid="ia-insights">
        {d.insights.length ? d.insights.map((x, i) => {
          const I = ICON[x.tipo] || Sparkles;
          return (
            <Rise key={i} i={i}><Glass className="p-4 border-cyan-500/25 shadow-[0_0_25px_rgba(6,182,212,0.08)]" data-testid={`ia-insight-${i}`}>
              <p className={`text-[10px] font-bold tracking-[0.16em] uppercase flex items-center gap-1.5 ${COLOR[x.tipo] || "text-cyan-300"}`}><I className="w-3.5 h-3.5" />Insight IA · {x.tipo}</p>
              <p className="mt-2 text-[15px] font-semibold leading-snug">{x.titulo}</p>
              <p className="mt-1 text-xs text-slate-400 leading-relaxed">{x.detalle}</p>
            </Glass></Rise>
          );
        }) : <p className="text-xs text-slate-500">La IA necesita algunas ventas para generar insights.</p>}
      </div>
    )}
  </>);
};

const Chat = () => {
  const [sid] = useState(() => localStorage.getItem("aipos_movil_chat") || (() => { const s = `m${Date.now()}`; localStorage.setItem("aipos_movil_chat", s); return s; })());
  const [msgs, setMsgs] = useState([]);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get(`/mobile/ia/chat/${sid}`).then((r) => setMsgs(r.data.mensajes.map((m) => ({ rol: m.rol, texto: m.texto })))); }, [sid]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const send = async (q) => {
    const mensaje = (q ?? txt).trim();
    if (!mensaje || busy) return;
    setTxt(""); setBusy(true);
    setMsgs((m) => [...m, { rol: "user", texto: mensaje }, { rol: "assistant", texto: "" }]);
    try {
      const res = await fetch(`${API_BASE}/mobile/ia/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("aipos_token")}` },
        body: JSON.stringify({ session_id: sid, mensaje }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          try {
            const j = JSON.parse(line);
            const add = j.t || (j.error ? `\n${j.error}` : "");
            setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], texto: c[c.length - 1].texto + add }; return c; });
          } catch (_) { /* fragmento incompleto */ }
        }
      }
    } catch (_) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { rol: "assistant", texto: "No pude responder ahora. Revisa tu conexión." }; return c; });
    } finally { setBusy(false); }
  };

  return (<>
    <SectionTitle right={<Bot className="w-4 h-4 text-cyan-400" />}>Pregúntale a tu negocio</SectionTitle>
    <Glass className="p-3" data-testid="ia-chat">
      <div className="space-y-2 max-h-[46dvh] overflow-y-auto pr-1">
        {!msgs.length && (
          <div className="flex flex-wrap gap-2 p-1">
            {SUGERIDAS.map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`ia-sugerida-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-cyan-500/30 text-cyan-200 bg-cyan-500/5">{s}</button>)}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.rol === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`ia-msg-${m.rol}-${i}`} className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${m.rol === "user" ? "bg-cyan-500 text-[#04121A] rounded-br-md" : "bg-[#0F1A30] text-slate-200 rounded-bl-md border border-white/5"}`}>
              {m.texto.replace(/\*\*/g, "") || <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-bounce [animation-delay:240ms]" /></span>}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input data-testid="ai-ask-input" value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Ej: ¿qué vendí más esta semana?"
               className="flex-1 h-11 rounded-full bg-[#050912] border border-white/10 px-4 text-sm outline-none focus:border-cyan-400/50 transition-colors duration-200" />
        <button data-testid="ai-ask-send" disabled={busy || !txt.trim()} className="w-11 h-11 rounded-full bg-cyan-500 text-[#04121A] grid place-items-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </form>
    </Glass>
  </>);
};

export default function IA() {
  const { me } = useMobile();
  return (
    <div className="pt-5" data-testid="mobile-ia">
      <h1 className="text-2xl font-bold font-display flex items-center gap-2"><Sparkles className="w-6 h-6 text-cyan-400" />Inteligencia Ai-POS</h1>
      <p className="text-xs text-slate-400 mt-1">Análisis con IA sobre las ventas e inventario reales de tu negocio.</p>
      {me.tier === "PRO" ? (<><Insights /><Chat /></>) : (
        <div className="mt-5 space-y-3">
          <ProLock testId="ia-pro-lock-insights" titulo="Insights diarios de IA" detalle="Combos que suben tu ticket, horas peak, productos a reponer y alertas que nadie más ve." />
          <ProLock testId="ia-pro-lock-chat" titulo="Pregúntale a tu negocio" detalle="Un asistente que responde con tus cifras reales: qué vender, cuándo reforzar personal y qué promocionar." />
        </div>
      )}
    </div>
  );
}
